import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import apiService from '../services/api';

const NotificationSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [settingData, setSettingData] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        user: {
            upcoming_platform_maintenance: false,
            user_account_changes: false,
            transaction_failures: false,
            transcation_success: false
        },
        admin: {
            account_auto_lock: true,
            account_expiry: true,
            notification_channels: {
                email: true,
                sms: false,
                in_platform: true,
                whatsapp: false
            }
        }
    });

    useEffect(() => {
        loadNotificationSettings();
    }, []);

    const loadNotificationSettings = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiService.getOrganizationDefaultSettingByCode('notification_settings');
            if (response.success && response.data) {
                setSettingData(response.data);
                const settings = response.data.value || {};

                setFormData({
                    user: {
                        upcoming_platform_maintenance: settings.user?.upcoming_platform_maintenance || false,
                        user_account_changes: settings.user?.user_account_changes || false,
                        transaction_failures: settings.user?.transaction_failures || false,
                        transcation_success: settings.user?.transcation_success || false
                    },
                    admin: {
                        account_auto_lock: settings.admin?.account_auto_lock !== undefined ? settings.admin.account_auto_lock : true,
                        account_expiry: settings.admin?.account_expiry !== undefined ? settings.admin.account_expiry : true,
                        notification_channels: {
                            email: settings.admin?.notification_channels?.email !== undefined ? settings.admin.notification_channels.email : true,
                            sms: settings.admin?.notification_channels?.sms || false,
                            in_platform: settings.admin?.notification_channels?.in_platform !== undefined ? settings.admin.notification_channels.in_platform : true,
                            whatsapp: settings.admin?.notification_channels?.whatsapp || false
                        }
                    }
                });
            } else {
                setError(response.error || 'Failed to load notification settings');
            }
        } catch (error) {
            console.error('Error loading notification settings:', error);
            setError('Failed to load notification settings');
        } finally {
            setLoading(false);
        }
    };

    const handleUserNotificationChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            user: {
                ...prev.user,
                [name]: checked
            }
        }));
    };

    const handleAdminNotificationChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            admin: {
                ...prev.admin,
                [name]: checked
            }
        }));
    };

    const handleChannelChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            admin: {
                ...prev.admin,
                notification_channels: {
                    ...prev.admin.notification_channels,
                    [name]: checked
                }
            }
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const updateData = {
                name: 'Notification Settings',
                code: 'notification_settings',
                value: formData
            };

            let response;
            if (settingData && settingData.id) {
                // Update existing setting
                response = await apiService.updateOrganizationDefaultSetting(settingData.id, updateData);
            } else {
                // Create new setting
                response = await apiService.createOrganizationDefaultSetting(updateData);
            }

            if (response.success) {
                setSuccess('Notification settings saved successfully!');
                setSettingData(response.data);
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.error || 'Failed to save notification settings');
            }
        } catch (error) {
            console.error('Error saving notification settings:', error);
            setError('Failed to save notification settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <div className="mt-2">Loading notification settings...</div>
            </div>
        );
    }

    return (
        <div>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>
                            <i className="bi bi-bell me-2"></i>
                            Notification Settings
                        </h2>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" dismissible onClose={() => setError('')}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {success && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                            <i className="bi bi-check-circle me-2"></i>
                            {success}
                        </Alert>
                    </Col>
                </Row>
            )}

            <Form onSubmit={handleSave}>
                <Row>
                    <Col lg={8}>
                        {/* User Notifications */}
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <i className="bi bi-person me-2"></i>
                                    User Notifications
                                </h5>
                                <small className="text-muted">Default notification preferences for regular users</small>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="upcoming_platform_maintenance"
                                                name="upcoming_platform_maintenance"
                                                checked={formData.user.upcoming_platform_maintenance}
                                                onChange={handleUserNotificationChange}
                                                label="Upcoming Platform Maintenance"
                                            />
                                            <Form.Text className="text-muted">
                                                Notify users about scheduled system maintenance
                                            </Form.Text>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="user_account_changes"
                                                name="user_account_changes"
                                                checked={formData.user.user_account_changes}
                                                onChange={handleUserNotificationChange}
                                                label="User Account Changes"
                                            />
                                            <Form.Text className="text-muted">
                                                Notify users when their account details are modified
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="transaction_failures"
                                                name="transaction_failures"
                                                checked={formData.user.transaction_failures}
                                                onChange={handleUserNotificationChange}
                                                label="Transaction Failures"
                                            />
                                            <Form.Text className="text-muted">
                                                Notify users when transactions fail
                                            </Form.Text>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="transcation_success"
                                                name="transcation_success"
                                                checked={formData.user.transcation_success}
                                                onChange={handleUserNotificationChange}
                                                label="Transaction Success"
                                            />
                                            <Form.Text className="text-muted">
                                                Notify users when transactions are successful
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Admin Notifications */}
                        <Card className="mb-4">
                            <Card.Header>
                                <h5 className="mb-0">
                                    <i className="bi bi-shield-check me-2"></i>
                                    Admin Notifications
                                </h5>
                                <small className="text-muted">System notifications for administrators</small>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="account_auto_lock"
                                                name="account_auto_lock"
                                                checked={formData.admin.account_auto_lock}
                                                onChange={handleAdminNotificationChange}
                                                label="Account Auto Lock"
                                            />
                                            <Form.Text className="text-muted">
                                                Notify admins when accounts are automatically locked
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="account_expiry"
                                                name="account_expiry"
                                                checked={formData.admin.account_expiry}
                                                onChange={handleAdminNotificationChange}
                                                label="Account Expiry"
                                            />
                                            <Form.Text className="text-muted">
                                                Notify admins about upcoming account expirations
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr />

                                <h6 className="mb-3">
                                    <i className="bi bi-broadcast me-2"></i>
                                    Notification Channels
                                </h6>

                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="email"
                                                name="email"
                                                checked={formData.admin.notification_channels.email}
                                                onChange={handleChannelChange}
                                                label={<><i className="bi bi-envelope me-1"></i>Email</>}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="sms"
                                                name="sms"
                                                checked={formData.admin.notification_channels.sms}
                                                onChange={handleChannelChange}
                                                label={<><i className="bi bi-phone me-1"></i>SMS</>}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="in_platform"
                                                name="in_platform"
                                                checked={formData.admin.notification_channels.in_platform}
                                                onChange={handleChannelChange}
                                                label={<><i className="bi bi-bell-fill me-1"></i>In-Platform</>}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="whatsapp"
                                                name="whatsapp"
                                                checked={formData.admin.notification_channels.whatsapp}
                                                onChange={handleChannelChange}
                                                label={<><i className="bi bi-whatsapp me-1"></i>WhatsApp</>}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={4}>
                        <Card className="bg-light mb-4">
                            <Card.Header>
                                <h6 className="mb-0">
                                    <i className="bi bi-info-circle me-2"></i>
                                    About Notification Settings
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                <p className="mb-2">
                                    <strong>User Notifications:</strong> Control default notification preferences for regular platform users.
                                </p>
                                <p className="mb-2">
                                    <strong>Admin Notifications:</strong> System-level notifications that administrators receive about security events and system status.
                                </p>
                                <p className="mb-0">
                                    <strong>Notification Channels:</strong> Select which communication channels to use for sending admin notifications.
                                </p>
                            </Card.Body>
                        </Card>

                        <Card className="border-warning">
                            <Card.Header className="bg-warning text-dark">
                                <h6 className="mb-0">
                                    <i className="bi bi-exclamation-triangle me-2"></i>
                                    Important Notes
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                <ul className="mb-0">
                                    <li className="mb-2">Email notifications require SMTP configuration</li>
                                    <li className="mb-2">SMS and WhatsApp require third-party service setup</li>
                                    <li className="mb-0">At least one admin notification channel should be enabled</li>
                                </ul>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Card>
                            <Card.Footer>
                                <div className="d-flex justify-content-end">
                                    <Button variant="primary" type="submit" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-lg me-2"></i>
                                                Save Notification Settings
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Card.Footer>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default NotificationSettings;